import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import GlobalHeader from "../../../components/GlobalHeader";
import SnackAlert from "../../../components/SnackAlert";
import useApi from "../../../hooks/useApi";
import useSnack from "../../../hooks/useSnack";
import { hrefSeguro } from "../../../utils/hrefSeguro";
import { ESTILO_VENCIDO, etiquetaVencido } from "../../../utils/estadoEnvio";

// Envíos de toda la plataforma y las correcciones del equipo.
//
// Las acciones aparecen solo cuando un artista reporta un envío:
//   - «No procede»: el artista recibe la respuesta.
//   - «Pasar a Social»: le pide una corrección al difusor, con un mensaje fijo
//     (Backend/src/utils/correccionEnvio.js). Tiene 3 días para entregar
//     links nuevos o rechazarla; si la rechaza o vence, pasa a Social solo.
//     Cuando la entrega, el admin la aprueba o la pasa a Social.
//   - «Anular»: con un mensaje para el difusor.
// Un envío resuelto se queda en la lista, marcado como resuelto.

const ESTADOS = {
  pending: { label: "Pendiente", color: "warning" },
  approve: { label: "Aprobado", color: "success" },
  rejected: { label: "Rechazado", color: "error" },
  anulado: { label: "Anulado", color: "default" },
  // El difusor no respondió a tiempo: el artista tiene unos días para
  // reenviarlo (ver Backend/src/utils/reenvios.js).
  vencido: { label: "Vencido", color: "default" },
};

// Con mensaje del admin. «Pasar a Social» va por la corrección (abajo).
const ACCIONES = {
  anular: {
    titulo: "Anular envío",
    boton: "Anular",
  },
};

const MENSAJE_MIN = 10;

// Respuesta al artista cuando su reporte no procede (editable).
const MENSAJE_NO_PROCEDE =
  "Checamos tu petición del reporte y el contenido del difusor fue el correcto.";

const ESTADOS_REPORTE = {
  pendiente: { icono: "🚩", label: "Reporte por revisar", color: "warning.main" },
  aprobado: { icono: "✅", label: "Reporte resuelto: procedió", color: "success.main" },
  rechazado: { icono: "✅", label: "Reporte resuelto: no procedió", color: "text.secondary" },
  corregido: { icono: "✅", label: "Reporte resuelto: el difusor corrigió", color: "success.main" },
};

// La corrección que se le pidió al difusor (reporte en "correccion").
const ESTADOS_CORRECCION = {
  solicitada: { label: "Corrección pedida al difusor", color: "info.main" },
  aceptada: { label: "El difusor aceptó corregir", color: "info.main" },
  entregada: { label: "Corrección entregada: revísala", color: "warning.main" },
};
// Cómo terminó, si pasó a Social.
const CIERRE_CORRECCION = {
  rechazada: "El difusor rechazó la corrección.",
  vencida: "Venció el plazo para corregir.",
  no_aprobada: "La corrección entregada no cumplió.",
};

const etiquetaReporte = (reporte) =>
  reporte.estado === "correccion"
    ? {
        icono: "🛠",
        ...(ESTADOS_CORRECCION[reporte.correccion?.estado] || {
          label: "En corrección",
          color: "info.main",
        }),
      }
    : ESTADOS_REPORTE[reporte.estado] || { icono: "", label: reporte.estado };

const fecha = (iso) =>
  new Date(iso).toLocaleString("es", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

// Solo se corrige un envío con un reporte del artista por revisar.
const porRevisar = (fila) => fila.reporte?.estado === "pendiente";
// Una corrección que el difusor ya entregó espera la revisión del admin.
const entregada = (fila) =>
  fila.reporte?.estado === "correccion" &&
  fila.reporte.correccion?.estado === "entregada";

// Lo que va a pasar con el dinero, para que el admin lo vea antes de
// confirmar. Mismo cálculo que el servidor (utils/ajustesEnvio.js).
function efectos(fila, accion) {
  const rc = Number(fila.radarCoin) || 0;
  const cobrado = fila.status === "approve" ? Number(fila.amount) || 0 : 0;
  if (accion === "social") {
    const descontar = cobrado > 0 ? Math.max(cobrado - 1, 0) : 0;
    return {
      artista: `Se le devuelven ${rc - 1} RC al artista.`,
      difusor:
        fila.status === "approve"
          ? `Al difusor se le descuentan ${descontar} USD (cobra 1 USD en vez de ${cobrado}).`
          : "El envío sigue pendiente, ahora como Envío Social: si el difusor lo aprueba, cobra 1 USD.",
      descontar,
    };
  }
  return {
    artista: `Se le devuelven los ${rc} RC al artista.`,
    difusor:
      fila.status === "approve"
        ? `Al difusor se le descuentan los ${cobrado} USD que cobró.`
        : "El envío deja de estar pendiente para el difusor.",
    descontar: cobrado,
  };
}

export default function EnviosAdmin() {
  const { loadApi, loadingApi } = useApi();
  const { msgSB, openSB, showSnack, typeSB } = useSnack();
  const [filas, setFilas] = useState([]);
  const [limite, setLimite] = useState(200);
  const [cargando, setCargando] = useState(true);
  const [busqueda, setBusqueda] = useState("");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  // Todos los planes: con "5 RC", un envío recién pasado a Social (1 RC)
  // desaparecería de la lista al recargar.
  const [rc, setRc] = useState("");
  const [objetivo, setObjetivo] = useState(null); // { fila, accion }
  const [mensaje, setMensaje] = useState("");
  // Reportes de artistas: filtro, cuántos esperan revisión y el diálogo de
  // "no procede".
  const [reporte, setReporte] = useState("");
  const [reportesPendientes, setReportesPendientes] = useState(0);
  const [correccionesEntregadas, setCorreccionesEntregadas] = useState(0);
  const [noProcede, setNoProcede] = useState(null); // fila
  const [respuesta, setRespuesta] = useState(MENSAJE_NO_PROCEDE);
  // «Pasar a Social» con reporte: pedir la corrección (fila).
  const [pidiendo, setPidiendo] = useState(null);
  // Revisar una corrección entregada: { fila, accion: "aprobar" | "rechazar" }.
  const [revisando, setRevisando] = useState(null);

  const cargar = () => {
    setCargando(true);
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (status) params.set("status", status);
    if (rc) params.set("rc", rc);
    if (reporte) params.set("reporte", reporte);
    loadApi(`admin/envios?${params.toString()}`, true, "get")
      .then((res) => {
        setFilas(res.data.filas || []);
        setLimite(res.data.limite || 200);
        setReportesPendientes(res.data.reportesPendientes || 0);
        setCorreccionesEntregadas(res.data.correccionesEntregadas || 0);
      })
      .catch((e) => showSnack(e.message, "error"))
      .finally(() => setCargando(false));
  };

  useEffect(() => {
    cargar();
  }, [q, status, rc, reporte]);

  // Todos los reportados, con los por revisar primero: uno resuelto se queda
  // a la vista, marcado como resuelto.
  const verReportes = () => {
    setReporte("todos");
    setRc("");
    setStatus("");
  };

  const marcarNoProcede = () =>
    loadApi(`admin/envios/${noProcede.id}/reporte/no-procede`, true, "post", {
      mensaje: respuesta.trim(),
    })
      .then((res) => {
        showSnack(res.data.message);
        setFilas((prev) =>
          prev.map((f) => (f.id === noProcede.id ? { ...f, reporte: res.data.reporte } : f))
        );
        setReportesPendientes((n) => Math.max(n - 1, 0));
        setNoProcede(null);
      })
      .catch((e) => showSnack(e.message, "error"));

  const abrir = (fila, accion) => {
    setObjetivo({ fila, accion });
    setMensaje("");
  };

  const confirmar = () =>
    loadApi(`admin/envios/${objetivo.fila.id}/ajuste`, true, "post", {
      accion: objetivo.accion,
      mensaje,
    })
      .then((res) => {
        showSnack(res.data.message);
        setObjetivo(null);
        // Se actualiza la fila en su lugar: recargando, con el filtro de 5
        // RC un envío recién pasado a Social desaparecería de la vista.
        const { envio, saldoDifusor } = res.data;
        if (objetivo.fila.reporte?.estado === "pendiente" && envio?.reporte)
          setReportesPendientes((n) => Math.max(n - 1, 0));
        setFilas((prev) =>
          prev.map((f) => {
            if (f.id === envio?.id) return { ...f, ...envio, saldoDifusor };
            return f.difusor === objetivo.fila.difusor ? { ...f, saldoDifusor } : f;
          })
        );
      })
      .catch((e) => showSnack(e.message, "error"));

  // La fila con lo que devolvió el servidor, sin recargar la lista.
  const actualizarFila = (envio, saldoDifusor, difusor) =>
    setFilas((prev) =>
      prev.map((f) => {
        if (f.id === envio?.id)
          return {
            ...f,
            ...envio,
            ...(saldoDifusor !== undefined ? { saldoDifusor } : {}),
            correccionPropuesta: null,
          };
        return saldoDifusor !== undefined && f.difusor === difusor
          ? { ...f, saldoDifusor }
          : f;
      })
    );

  const pedirCorreccion = () =>
    loadApi(`admin/envios/${pidiendo.id}/correccion`, true, "post", {})
      .then((res) => {
        showSnack(res.data.message);
        actualizarFila(res.data.envio);
        setReportesPendientes((n) => Math.max(n - 1, 0));
        setPidiendo(null);
      })
      .catch((e) => showSnack(e.message, "error"));

  const resolverEntrega = () =>
    loadApi(
      `admin/envios/${revisando.fila.id}/correccion/${revisando.accion}`,
      true,
      "post",
      {}
    )
      .then((res) => {
        showSnack(res.data.message);
        actualizarFila(res.data.envio, res.data.saldoDifusor, revisando.fila.difusor);
        setCorreccionesEntregadas((n) => Math.max(n - 1, 0));
        setRevisando(null);
      })
      .catch((e) => showSnack(e.message, "error"));

  const efecto = objetivo ? efectos(objetivo.fila, objetivo.accion) : null;
  const efectoSocial =
    revisando?.accion === "rechazar" ? efectos(revisando.fila, "social") : null;
  const saldoFinal =
    objetivo && objetivo.fila.saldoDifusor !== null
      ? objetivo.fila.saldoDifusor - efecto.descontar
      : null;

  return (
    <>
      <GlobalHeader />
      <Container>
        <Stack gap={2} pb={6}>
          <Typography variant="h4">Envíos</Typography>
          <Typography color="text.secondary">
            Las acciones aparecen cuando un artista reporta un envío. «Pasar a
            Social» le pide una corrección al difusor: tiene 3 días para
            entregar links nuevos o rechazarla (si la rechaza o vence, pasa a
            Social y al artista se le devuelven sus RC); cuando la entrega, tú
            la apruebas o la pasas a Social. «Anular» le devuelve todo al
            artista. Si el reporte no procede, el artista recibe tu respuesta.
            Los reportes por revisar aparecen primero; los resueltos se quedan
            en la lista, marcados como resueltos.
          </Typography>

          {reportesPendientes + correccionesEntregadas > 0 && !reporte && (
            <Alert
              severity="warning"
              action={
                <Button color="inherit" size="small" onClick={verReportes}>
                  Ver reportes
                </Button>
              }
            >
              {[
                reportesPendientes === 1
                  ? "1 reporte de un artista por revisar"
                  : reportesPendientes > 1 &&
                    `${reportesPendientes} reportes de artistas por revisar`,
                correccionesEntregadas === 1
                  ? "1 corrección entregada por revisar"
                  : correccionesEntregadas > 1 &&
                    `${correccionesEntregadas} correcciones entregadas por revisar`,
              ]
                .filter(Boolean)
                .join(" y ")
                .replace(/^./, (l) => `Hay ${l}`)}
              .
            </Alert>
          )}

          <Stack
            direction={{ xs: "column", sm: "row" }}
            gap={1}
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              setQ(busqueda.trim());
            }}
          >
            <TextField
              size="small"
              label="Buscar"
              placeholder="Artista, difusor, canción o evento"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              sx={{ flex: 1 }}
            />
            <TextField
              select
              size="small"
              label="Plan"
              // Sin filtro se lee «Todos», en vez de un campo vacío.
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
              value={rc}
              onChange={(e) => setRc(e.target.value)}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="5">5 RC</MenuItem>
              <MenuItem value="1">1 RC</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Estado"
              // Sin filtro se lee «Todos», en vez de un campo vacío.
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">Todos</MenuItem>
              {Object.entries(ESTADOS).map(([valor, { label }]) => (
                <MenuItem key={valor} value={valor}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Reportes"
              // Sin filtro se lee «Todos», en vez de un campo vacío.
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
              value={reporte}
              onChange={(e) => setReporte(e.target.value)}
              sx={{ minWidth: 170 }}
            >
              <MenuItem value="">Todos los envíos</MenuItem>
              <MenuItem value="todos">Reportados</MenuItem>
              <MenuItem value="abiertos">
                Por revisar o en corrección
                {reportesPendientes + correccionesEntregadas > 0
                  ? ` (${reportesPendientes + correccionesEntregadas} por revisar)`
                  : ""}
              </MenuItem>
            </TextField>
            <Button type="submit" variant="contained">
              Buscar
            </Button>
          </Stack>

          {!cargando && filas.length >= limite && (
            <Alert severity="info">
              Se muestran los {limite} envíos más recientes. Usa la búsqueda
              para encontrar uno más viejo.
            </Alert>
          )}

          <TableContainer component={Paper}>
            <Table size="small" sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Envío</TableCell>
                  <TableCell>Artista</TableCell>
                  <TableCell>Difusor</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Publicaciones</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cargando ? (
                  <TableRow>
                    <TableCell colSpan={8}>Cargando…</TableCell>
                  </TableRow>
                ) : filas.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8}>No hay envíos con ese filtro.</TableCell>
                  </TableRow>
                ) : (
                  filas.map((fila) => {
                    const ultimo = fila.ajustes[fila.ajustes.length - 1];
                    return (
                      <TableRow key={fila.id}>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {fecha(fila.fecha)}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{fila.titulo}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {fila.tipo === "evento" ? "Evento" : "Canción"}
                            {fila.directLink && ` · Direct Link: ${fila.directLink}`}
                          </Typography>
                        </TableCell>
                        <TableCell>{fila.artista}</TableCell>
                        <TableCell>{fila.difusor}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {fila.radarCoin} RC · {fila.amount} USD
                          {ultimo?.antes &&
                            Number(ultimo.antes.radarCoin) !== Number(fila.radarCoin) && (
                              <Typography variant="caption" color="text.secondary" component="div">
                                antes {ultimo.antes.radarCoin} RC
                              </Typography>
                            )}
                        </TableCell>
                        <TableCell>
                          <Stack gap={0.5} alignItems="flex-start">
                            <Chip
                              size="small"
                              label={
                                fila.status === "vencido"
                                  ? etiquetaVencido(fila.reenviado)
                                  : ESTADOS[fila.status]?.label || fila.status
                              }
                              color={ESTADOS[fila.status]?.color || "default"}
                              variant={fila.status === "vencido" && fila.reenviado ? "outlined" : "filled"}
                              sx={fila.status === "vencido" && !fila.reenviado ? ESTILO_VENCIDO : undefined}
                            />
                            {fila.reporte && (
                              <Box
                                sx={{
                                  mt: 0.5,
                                  p: 1,
                                  maxWidth: 260,
                                  borderLeft: "3px solid",
                                  borderColor: etiquetaReporte(fila.reporte).color,
                                  bgcolor: "rgba(242, 114, 36, 0.06)",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  component="div"
                                  fontWeight={700}
                                  color={etiquetaReporte(fila.reporte).color}
                                >
                                  {etiquetaReporte(fila.reporte).icono}{" "}
                                  {etiquetaReporte(fila.reporte).label}
                                  {fila.reporte.estado === "aprobado" &&
                                    fila.reporte.devueltoRC > 0 &&
                                    ` · ${fila.reporte.devueltoRC} RC devueltos`}
                                </Typography>
                                <Typography variant="caption" component="div" sx={{ whiteSpace: "pre-wrap" }}>
                                  «{fila.reporte.mensaje}»
                                </Typography>
                                {fila.reporte.respuesta && (
                                  <Typography variant="caption" component="div" color="text.secondary">
                                    Respuesta: {fila.reporte.respuesta}
                                  </Typography>
                                )}
                                <EstadoCorreccion reporte={fila.reporte} />
                              </Box>
                            )}
                            {ultimo && (
                              <Tooltip title={ultimo.mensaje}>
                                <Typography variant="caption" color="text.secondary">
                                  {ultimo.accion === "social"
                                    ? "Reclasificado a Social"
                                    : "Anulado"}{" "}
                                  el {fecha(ultimo.fecha)}
                                </Typography>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 220 }}>
                          {fila.urls_post.length === 0 ? (
                            <Typography variant="caption" color="text.secondary">
                              —
                            </Typography>
                          ) : (
                            fila.urls_post.map((link) => (
                              <Box key={link} sx={{ overflowWrap: "anywhere" }}>
                                <a
                                  href={hrefSeguro(link)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ fontSize: 12 }}
                                >
                                  {link}
                                </a>
                              </Box>
                            ))
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {porRevisar(fila) && (
                            <Stack direction="row" gap={1} justifyContent="flex-end" flexWrap="wrap">
                              <Button
                                size="small"
                                variant="outlined"
                                color="inherit"
                                onClick={() => {
                                  setNoProcede(fila);
                                  setRespuesta(MENSAJE_NO_PROCEDE);
                                }}
                              >
                                No procede
                              </Button>
                              {fila.correccionPropuesta && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => setPidiendo(fila)}
                                >
                                  Pasar a Social
                                </Button>
                              )}
                              <Button
                                size="small"
                                color="error"
                                variant="outlined"
                                onClick={() => abrir(fila, "anular")}
                              >
                                Anular
                              </Button>
                            </Stack>
                          )}
                          {entregada(fila) && (
                            <Stack direction="row" gap={1} justifyContent="flex-end" flexWrap="wrap">
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                onClick={() => setRevisando({ fila, accion: "aprobar" })}
                              >
                                Aprobar corrección
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setRevisando({ fila, accion: "rechazar" })}
                              >
                                Pasar a Social
                              </Button>
                            </Stack>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </Container>

      <Dialog
        open={Boolean(objetivo)}
        onClose={() => setObjetivo(null)}
        fullWidth
        maxWidth="sm"
      >
        {objetivo && (
          <>
            <DialogTitle>{ACCIONES[objetivo.accion].titulo}</DialogTitle>
            <DialogContent>
              <Stack gap={2} pt={1}>
                <Typography>
                  <strong>{objetivo.fila.titulo}</strong> de{" "}
                  {objetivo.fila.artista} a {objetivo.fila.difusor} (
                  {objetivo.fila.radarCoin} RC,{" "}
                  {ESTADOS[objetivo.fila.status]?.label.toLowerCase()}).
                </Typography>
                {objetivo.fila.reporte?.estado === "pendiente" && (
                  <Alert severity="info">
                    El artista reportó este envío. Al confirmar, su reporte
                    queda aprobado y se le avisa que se le devolvieron sus RC.
                  </Alert>
                )}
                <Alert severity="warning">
                  <div>{efecto.artista}</div>
                  <div>{efecto.difusor}</div>
                  {saldoFinal !== null && saldoFinal < 0 && (
                    <div>
                      El difusor quedará con saldo {saldoFinal} USD: se
                      descuenta de su próximo cobro.
                    </div>
                  )}
                  {objetivo.fila.directLink && (
                    <div>
                      Viene de un Direct Link: lo que se devuelve son RC a la
                      cuenta del artista. Si pagó con PayPal y prefiere el
                      reembolso en dinero, hazlo desde PayPal en lugar de usar
                      esta opción.
                    </div>
                  )}
                </Alert>
                <TextField
                  label="Mensaje para el difusor"
                  placeholder="Ej.: El envío era una nota editorial y se publicó solo un post de Instagram."
                  value={mensaje}
                  onChange={(e) => setMensaje(e.target.value)}
                  multiline
                  minRows={3}
                  inputProps={{ maxLength: 1000 }}
                  helperText={`Le llega por notificación y correo. ${mensaje.trim().length}/1000 (mínimo ${MENSAJE_MIN})`}
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setObjetivo(null)}>Cancelar</Button>
              <LoadingButton
                variant="contained"
                color={objetivo.accion === "anular" ? "error" : "primary"}
                disabled={mensaje.trim().length < MENSAJE_MIN}
                loading={loadingApi.includes(
                  `post__admin/envios/${objetivo.fila.id}/ajuste`
                )}
                onClick={confirmar}
              >
                {ACCIONES[objetivo.accion].boton}
              </LoadingButton>
            </DialogActions>
          </>
        )}
      </Dialog>
      {/* Pasar a Social = pedir la corrección al difusor */}
      <Dialog open={Boolean(pidiendo)} onClose={() => setPidiendo(null)} fullWidth maxWidth="sm">
        {pidiendo && (
          <>
            <DialogTitle>Pasar a Social: pedir corrección al difusor</DialogTitle>
            <DialogContent>
              <Stack gap={2} pt={1}>
                <Typography>
                  <strong>{pidiendo.titulo}</strong> de {pidiendo.artista} a{" "}
                  {pidiendo.difusor} ({pidiendo.radarCoin} RC).
                </Typography>
                <Alert severity="info">
                  No pasa a Social de inmediato. El difusor tiene{" "}
                  {pidiendo.correccionPropuesta.plazoHoras / 24} días para
                  aceptar y entregar links nuevos, o rechazarlo. Si lo rechaza
                  o vence el plazo, pasa a Social solo: al artista se le
                  devuelven {pidiendo.correccionPropuesta.devolverRC} RC y al
                  difusor se le descuentan {efectos(pidiendo, "social").descontar}{" "}
                  USD. Si entrega, tú revisas los links y la apruebas o la pasas
                  a Social.
                </Alert>
                <Box sx={{ p: 1.5, bgcolor: "rgba(242, 114, 36, 0.06)", borderLeft: "3px solid", borderColor: "primary.main" }}>
                  <Typography variant="caption" color="text.secondary">
                    Mensaje que recibe el difusor (notificación y correo):
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {pidiendo.correccionPropuesta.mensaje}
                  </Typography>
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPidiendo(null)}>Cancelar</Button>
              <LoadingButton
                variant="contained"
                loading={loadingApi.includes(`post__admin/envios/${pidiendo.id}/correccion`)}
                onClick={pedirCorreccion}
              >
                Enviar al difusor
              </LoadingButton>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Revisar la corrección entregada */}
      <Dialog open={Boolean(revisando)} onClose={() => setRevisando(null)} fullWidth maxWidth="sm">
        {revisando && (
          <>
            <DialogTitle>
              {revisando.accion === "aprobar"
                ? "Aprobar la corrección"
                : "La corrección no cumple: pasar a Social"}
            </DialogTitle>
            <DialogContent>
              <Stack gap={2} pt={1}>
                <Typography>
                  <strong>{revisando.fila.titulo}</strong> de {revisando.fila.artista} a{" "}
                  {revisando.fila.difusor}. Links que entregó:
                </Typography>
                <Box>
                  {(revisando.fila.reporte.correccion?.urls || []).map((link) => (
                    <Box key={link} sx={{ overflowWrap: "anywhere" }}>
                      <a href={hrefSeguro(link)} target="_blank" rel="noopener noreferrer">
                        {link}
                      </a>
                    </Box>
                  ))}
                </Box>
                {revisando.accion === "aprobar" ? (
                  <Alert severity="success">
                    El envío sigue siendo de {revisando.fila.radarCoin} RC y los
                    links nuevos se suman a sus publicaciones. Al artista le
                    llegan los links.
                  </Alert>
                ) : (
                  <Alert severity="warning">
                    <div>{efectoSocial.artista}</div>
                    <div>{efectoSocial.difusor}</div>
                    <div>El difusor recibe un aviso de que su corrección no cumplió.</div>
                  </Alert>
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setRevisando(null)}>Cancelar</Button>
              <LoadingButton
                variant="contained"
                color={revisando.accion === "aprobar" ? "success" : "primary"}
                loading={loadingApi.includes(
                  `post__admin/envios/${revisando.fila.id}/correccion/${revisando.accion}`
                )}
                onClick={resolverEntrega}
              >
                {revisando.accion === "aprobar" ? "Aprobar corrección" : "Pasar a Social"}
              </LoadingButton>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* El reporte no procede */}
      <Dialog open={Boolean(noProcede)} onClose={() => setNoProcede(null)} fullWidth maxWidth="sm">
        {noProcede && (
          <>
            <DialogTitle>El reporte no procede</DialogTitle>
            <DialogContent>
              <Stack gap={2} pt={1}>
                <Typography>
                  <strong>{noProcede.titulo}</strong> de {noProcede.artista} a{" "}
                  {noProcede.difusor}. El envío y el dinero no cambian.
                </Typography>
                <Box sx={{ p: 1, bgcolor: "rgba(0,0,0,0.04)", borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Lo que reportó el artista:
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {noProcede.reporte?.mensaje}
                  </Typography>
                </Box>
                <TextField
                  label="Mensaje para el artista"
                  value={respuesta}
                  onChange={(e) => setRespuesta(e.target.value)}
                  multiline
                  minRows={3}
                  inputProps={{ maxLength: 1000 }}
                  helperText={`Le llega por notificación y correo (mínimo ${MENSAJE_MIN} caracteres).`}
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setNoProcede(null)}>Cancelar</Button>
              <LoadingButton
                variant="contained"
                disabled={respuesta.trim().length < MENSAJE_MIN}
                loading={loadingApi.includes(
                  `post__admin/envios/${noProcede.id}/reporte/no-procede`
                )}
                onClick={marcarNoProcede}
              >
                Enviar respuesta
              </LoadingButton>
            </DialogActions>
          </>
        )}
      </Dialog>
      <SnackAlert openSB={openSB} typeSB={typeSB} msgSB={msgSB} />
    </>
  );
}

// Debajo del reporte: el plazo de la corrección, los links que entregó el
// difusor o cómo terminó.
function EstadoCorreccion({ reporte }) {
  const c = reporte.correccion;
  if (!c) return null;
  if (reporte.estado === "correccion" && c.estado !== "entregada")
    return (
      <Typography variant="caption" component="div" color="text.secondary">
        Tiene hasta el {fecha(c.vence)}.
      </Typography>
    );
  if (c.estado === "entregada" || c.estado === "aprobada")
    return (
      <Box mt={0.5}>
        <Typography variant="caption" component="div" color="text.secondary">
          Links que entregó:
        </Typography>
        {(c.urls || []).map((link) => (
          <Box key={link} sx={{ overflowWrap: "anywhere" }}>
            <a href={hrefSeguro(link)} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>
              {link}
            </a>
          </Box>
        ))}
      </Box>
    );
  return CIERRE_CORRECCION[c.estado] ? (
    <Typography variant="caption" component="div" color="text.secondary">
      {CIERRE_CORRECCION[c.estado]}
    </Typography>
  ) : null;
}
